"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, PhoneCall, Pipette, ShieldAlert, Waves, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const problemCategories = [{
  id: "burst-pipes",
  title: "Burst & Leaking Pipes",
  urgency: "Immediate Hazard",
  iconName: "pipette",
  description: "Active high-pressure line breaks, slab leaks, freezing-induced cracks, and ceiling drips causing structural water damage.",
  typicalSigns: "Hissing wall sounds, drop in pressure, sudden wet spots on drywall or floorboards.",
  targetUrl: "/problems/",
  badgeVariant: "destructive" as const
}, {
  id: "sewer-backups",
  title: "Sewer & Main Line Backups",
  urgency: "Biohazard Risk",
  iconName: "waves",
  description: "Raw sewage rising in floor drains, multiple simultaneous fixture blockages, or contaminated yard pooling.",
  typicalSigns: "Gurgling toilets, foul sewer gas odors, wastewater backing up into bathtubs when flushing.",
  targetUrl: "/problems/",
  badgeVariant: "destructive" as const
}, {
  id: "water-heaters",
  title: "Water Heater Leaks & Failures",
  urgency: "High Priority",
  iconName: "flame",
  description: "Ruptured tank bottoms, scalding temperature valve discharge, complete loss of hot water, or pilot ignition failure.",
  typicalSigns: "Rusty puddle around tank, popping noises, lukewarm water, damp insulation.",
  targetUrl: "/water-heater/",
  badgeVariant: "default" as const
}, {
  id: "severe-clogs",
  title: "Severe Drain Blockages",
  urgency: "Urgent Service",
  iconName: "wrench",
  description: "Stubborn kitchen grease traps, hair dams in main branches, and foreign object obstructions that plunge methods cannot clear.",
  typicalSigns: "Standing water in sinks, slow drainage across rooms, recurring localized pooling.",
  targetUrl: "/problems/",
  badgeVariant: "secondary" as const
}, {
  id: "toilet-overflows",
  title: "Toilet Overflows & Floods",
  urgency: "Immediate Hazard",
  iconName: "droplets",
  description: "Uncontrollable bowl fill-overs, stuck flappers causing endless basin filling, or broken shut-off angle stops.",
  typicalSigns: "Water spilling over rim, tank fill valve stuck wide open, leaking base seal onto floor.",
  targetUrl: "/problems/",
  badgeVariant: "destructive" as const
}, {
  id: "pressure-failures",
  title: "Low Pressure & Valve Faults",
  urgency: "Standard Emergency",
  iconName: "shield-alert",
  description: "Sudden home-wide pressure collapse, faulty pressure reducing valves (PRV), or seized main water shut-off valves.",
  typicalSigns: "Weak trickle at all fixtures, loud pipe banging (water hammer), frozen shut-off handles.",
  targetUrl: "/problems/",
  badgeVariant: "secondary" as const
}];
export default function ProblemCategories() {
  const prefersReducedMotion = useReducedMotion();
  const renderIcon = (name: string) => {
    switch (name) {
      case "pipette":
        return <Pipette className="size-6 text-primary" />;
      case "waves":
        return <Waves className="size-6 text-destructive" />;
      case "flame":
        return <Flame className="size-6 text-accent-foreground" />;
      case "wrench":
        return <Wrench className="size-6 text-primary" />;
      case "droplets":
        return <Droplets className="size-6 text-destructive" />;
      case "shield-alert":
      default:
        return <ShieldAlert className="size-6 text-primary" />;
    }
  };
  return <section id="relatedproblemcategories" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="inline-block size-2.5 rounded-full bg-accent" />
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Emergency Diagnosis Directory
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold font-default tracking-tight text-foreground">
            Explore Common Plumbing Emergencies
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Select an urgent category to view symptom checks, fast water shut-off instructions, and 24/7 dispatched repair services across Jonesboro, GA.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {problemCategories.map((item, i) => <div key={item.id} data-index={i}>
              <motion.div initial={prefersReducedMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={prefersReducedMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: prefersReducedMotion ? 0 : i * 0.05
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border bg-card hover:border-primary/40 hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="size-12 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                        {renderIcon(item.iconName)}
                      </div>
                      <Badge variant={item.badgeVariant} className="font-semibold text-2xs">
                        {item.urgency}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground line-clamp-2 mt-1">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 flex-1">
                    <div className="p-3 rounded-md bg-muted/60 border border-border/60 text-xs">
                      <div className="font-semibold text-foreground mb-1 flex items-center gap-1.5">
                        <AlertTriangle className="size-3.5 text-accent-foreground shrink-0" />
                        Common Warning Signs:
                      </div>
                      <p className="text-muted-foreground leading-normal">
                        {item.typicalSigns}
                      </p>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 border-t border-border/50 flex items-center justify-between">
                    <Button asChild variant="ghost" size="sm" className="px-0 hover:bg-transparent text-primary font-semibold hover:text-primary/80 group">
                      <Link to={item.targetUrl} className="inline-flex items-center gap-1.5">
                        View Solutions
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="sm" className="rounded-md">
                      <Link to="/contact/">
                        Call Help
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Urgent Bottom Notice / Quick Action */}
        <div className="mt-12 md:mt-16 rounded-xl bg-secondary p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border border-border">
          <div className="space-y-1">
            <h3 className="text-lg font-bold font-default text-secondary-foreground flex items-center gap-2">
              <PhoneCall className="size-5 text-primary" />
              Not sure which problem category matches your issue?
            </h3>
            <p className="text-sm text-muted-foreground">
              Speak directly with an on-call emergency plumber in Jonesboro to guide you through emergency shut-off steps.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto shadow-sm">
              <Link to="/contact/">
                Request Urgent Plumber
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/services/">
                All Services
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}